import React, { useState } from "react";
import InputField from "../input/inputField";
import { login } from "../../Services/users/users.service";
import { useDispatch } from "react-redux";
import { setErrorMessage } from "../../Redux/Slices/alert.slice";
import { EMAIL_REGEX } from "../../Constants/regex";
import {
  EMAIL_REQUIRED,
  INVALID_EMAIL_MESSAGE,
  PASSWORD_REQUIRED,
} from "../../Constants/messages";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../hooks/deviceHooks/useDevice.hooks";
import { Colors } from "../../Constants/colors";
import {
  DISPLAY,
  FLEX_DIRECTION,
  ALIGN_ITEMS,
  JUSTIFY_CONTENT,
  SPACING,
  FONT_SIZE,
} from "../../Constants/Enums/styles.enum";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";

interface Props {
  header: string;
  subHeader: string;
}

const LoginIllustration = () => (
  <svg
    width="100%"
    height="auto"
    viewBox="0 0 400 280"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ maxWidth: "300px", marginBottom: "24px" }}
  >
    {/* Background Gradient Circle */}
    <circle
      cx="200"
      cy="140"
      r="100"
      fill={Colors.TangerineOrange}
      fillOpacity="0.06"
    />
    <circle
      cx="200"
      cy="140"
      r="70"
      fill={Colors.TangerineOrange}
      fillOpacity="0.08"
    />

    {/* Computer Screen */}
    <rect
      x="110"
      y="80"
      width="180"
      height="120"
      rx="8"
      fill={Colors.White}
      stroke={Colors.TangerineOrange}
      strokeWidth="3"
    />
    <rect
      x="120"
      y="90"
      width="160"
      height="90"
      rx="4"
      fill={Colors.VeryLightGrey}
    />

    {/* Form Elements on Screen */}
    <rect
      x="135"
      y="105"
      width="90"
      height="6"
      rx="3"
      fill={Colors.TangerineOrange}
      fillOpacity="0.3"
    />
    <rect
      x="135"
      y="120"
      width="130"
      height="8"
      rx="4"
      fill={Colors.Grey}
      fillOpacity="0.2"
    />
    <rect
      x="135"
      y="135"
      width="130"
      height="8"
      rx="4"
      fill={Colors.Grey}
      fillOpacity="0.2"
    />
    <rect
      x="200"
      y="155"
      width="65"
      height="12"
      rx="6"
      fill={Colors.TangerineOrange}
    />

    {/* Computer Base */}
    <path
      d="M95 200 L105 208 L295 208 L305 200 Z"
      fill={Colors.TangerineOrange}
      fillOpacity="0.15"
    />

    {/* Floating Shield Icon */}
    <g transform="translate(280, 60)">
      <path
        d="M20 2 L10 6 L10 15 C10 22 20 26 20 26 C20 26 30 22 30 15 L30 6 Z"
        fill={Colors.CrimsonRed}
        fillOpacity="0.9"
      />
      <path
        d="M16 15 L18 17 L24 11"
        stroke={Colors.White}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </g>

    {/* Floating Email Icon */}
    <g transform="translate(85, 55)">
      <rect
        x="5"
        y="8"
        width="24"
        height="16"
        rx="2"
        fill={Colors.Success}
        fillOpacity="0.15"
        stroke={Colors.Success}
        strokeWidth="1.5"
      />
      <path
        d="M5 10 L17 16 L29 10"
        stroke={Colors.Success}
        strokeWidth="1.5"
        fill="none"
      />
    </g>

    {/* Checkmark Badge */}
    <g transform="translate(75, 175)">
      <circle cx="15" cy="15" r="15" fill={Colors.Success} fillOpacity="0.2" />
      <circle cx="15" cy="15" r="10" fill={Colors.Success} />
      <path
        d="M10 15 L13 18 L20 11"
        stroke={Colors.White}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </g>
  </svg>
);

const EmailIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
      stroke={Colors.TangerineOrange}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22 6L12 13L2 6"
      stroke={Colors.TangerineOrange}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="3"
      y="11"
      width="18"
      height="11"
      rx="2"
      stroke={Colors.TangerineOrange}
      strokeWidth="2"
    />
    <path
      d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
      stroke={Colors.TangerineOrange}
      strokeWidth="2"
    />
  </svg>
);

const Login = ({ header, subHeader }: Props) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const isMobile = useMobile();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validate = () => {
    if (!email) {
      dispatch(setErrorMessage(EMAIL_REQUIRED));
      return false;
    }
    if (!EMAIL_REGEX.test(email)) {
      dispatch(setErrorMessage(INVALID_EMAIL_MESSAGE));
      return false;
    }
    if (!password) {
      dispatch(setErrorMessage(PASSWORD_REQUIRED));
      return false;
    }
    return true;
  };

  const onLogin = () => {
    if (!validate()) return;
    login({ email, password })
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        dispatch(setErrorMessage(error.response.data.message));
      });
  };

  return (
    <div
      style={{
        display: DISPLAY.FLEX,
        flexDirection: FLEX_DIRECTION.COLUMN,
        gap: isMobile ? SPACING.S24 : SPACING.S32,
      }}
    >
      {/* School Name - Desktop Only */}
      {!isMobile && (
        <div style={{ textAlign: "center", marginBottom: SPACING.S16 }}>
          <h2
            style={{
              fontSize: FONT_SIZE.F24,
              fontFamily: FontFamily.POPPINS_BOLD,
              background: `linear-gradient(135deg, ${Colors.CrimsonRed} 0%, ${Colors.TangerineOrange} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              marginBottom: SPACING.S4,
              letterSpacing: "1px",
            }}
          >
            ROSE MOUNT PRE SCHOOL
          </h2>
          <div
            style={{
              width: "80px",
              height: "3px",
              background: `linear-gradient(90deg, ${Colors.CrimsonRed} 0%, ${Colors.TangerineOrange} 100%)`,
              margin: "0 auto",
              borderRadius: "2px",
            }}
          />
        </div>
      )}

      {/* Welcome Header */}
      <div style={{ textAlign: "center" }}>
        <LoginIllustration />
        <h1
          style={{
            fontSize: isMobile ? FONT_SIZE.F28 : FONT_SIZE.F36,
            fontFamily: FontFamily.POPPINS_BOLD,
            color: Colors.Black,
            marginBottom: SPACING.S12,
            background: `linear-gradient(135deg, ${Colors.CrimsonRed} 0%, ${Colors.TangerineOrange} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Welcome Back!
        </h1>
        <p
          style={{
            fontSize: isMobile ? FONT_SIZE.F14 : FONT_SIZE.F16,
            fontFamily: FontFamily.ROBOTO_REGULAR,
            color: Colors.Grey,
            lineHeight: "1.6",
          }}
        >
          Sign in to access your dashboard
        </p>
      </div>

      {/* Login Form */}
      <div
        style={{
          display: DISPLAY.FLEX,
          flexDirection: FLEX_DIRECTION.COLUMN,
          gap: SPACING.S24,
        }}
      >
        {/* Email Field */}
        <div>
          <div
            style={{
              display: DISPLAY.FLEX,
              alignItems: ALIGN_ITEMS.CENTER,
              gap: SPACING.S8,
              marginBottom: SPACING.S10,
            }}
          >
            <EmailIcon />
            <label
              style={{
                fontSize: FONT_SIZE.F14,
                fontFamily: FontFamily.POPPINS_REGULAR,
                color: Colors.Black,
                fontWeight: "500",
              }}
            >
              Email Address
            </label>
          </div>
          <InputField
            value={email}
            setValue={setEmail}
            label=""
            placeHolder="Enter your email address"
          />
        </div>

        {/* Password Field */}
        <div>
          <div
            style={{
              display: DISPLAY.FLEX,
              alignItems: ALIGN_ITEMS.CENTER,
              gap: SPACING.S8,
              marginBottom: SPACING.S10,
            }}
          >
            <LockIcon />
            <label
              style={{
                fontSize: FONT_SIZE.F14,
                fontFamily: FontFamily.POPPINS_REGULAR,
                color: Colors.Black,
                fontWeight: "500",
              }}
            >
              Password
            </label>
          </div>
          <InputField
            value={password}
            setValue={setPassword}
            label=""
            placeHolder="Enter your password"
            isPassword={true}
          />
        </div>

        {/* Security Notice */}
        <div
          style={{
            display: DISPLAY.FLEX,
            alignItems: ALIGN_ITEMS.CENTER,
            gap: SPACING.S12,
            padding: SPACING.S16,
            background: `linear-gradient(135deg, ${Colors.TangerineOrange}08 0%, ${Colors.CrimsonRed}05 100%)`,
            borderRadius: "12px",
            border: `1px solid ${Colors.LightGrey}`,
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z"
              fill={Colors.TangerineOrange}
              fillOpacity="0.2"
            />
            <path
              d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z"
              stroke={Colors.TangerineOrange}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 12L11 14L15 10"
              stroke={Colors.TangerineOrange}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p
            style={{
              fontSize: FONT_SIZE.F12,
              fontFamily: FontFamily.ROBOTO_REGULAR,
              color: Colors.DarkGrey,
              margin: 0,
              flex: 1,
            }}
          >
            <strong
              style={{
                color: Colors.Black,
                fontFamily: FontFamily.ROBOTO_BOLD,
              }}
            >
              Secure Connection:
            </strong>{" "}
            Your credentials are encrypted and protected
          </p>
        </div>

        {/* Login Button */}
        <div
          style={{
            marginTop: SPACING.S8,
          }}
        >
          <div
            style={{
              width: "100%",
              padding: SPACING.S16,
              background: `linear-gradient(135deg, ${Colors.TangerineOrange} 0%, ${Colors.CrimsonRed} 100%)`,
              borderRadius: "12px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: `0 4px 15px ${Colors.TangerineOrange}40`,
            }}
            onClick={onLogin}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 6px 25px ${Colors.TangerineOrange}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 4px 15px ${Colors.TangerineOrange}40`;
            }}
          >
            <div
              style={{
                display: DISPLAY.FLEX,
                alignItems: ALIGN_ITEMS.CENTER,
                justifyContent: JUSTIFY_CONTENT.CENTER,
                gap: SPACING.S10,
              }}
            >
              <span
                style={{
                  fontSize: FONT_SIZE.F16,
                  fontFamily: FontFamily.POPPINS_BOLD,
                  color: Colors.White,
                  letterSpacing: "0.5px",
                }}
              >
                Sign In to Dashboard
              </span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke={Colors.White}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Quick Access Features */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: SPACING.S12,
            marginTop: SPACING.S16,
            paddingTop: SPACING.S24,
            borderTop: `1px solid ${Colors.LightGrey}`,
          }}
        >
          {[
            { emoji: "📊", title: "Analytics", desc: "Track performance" },
            { emoji: "👥", title: "Students", desc: "Manage records" },
            { emoji: "📝", title: "Results", desc: "Grade management" },
            { emoji: "🔔", title: "Notifications", desc: "Stay updated" },
          ].map((item, index) => (
            <div
              key={index}
              style={{
                display: DISPLAY.FLEX,
                alignItems: ALIGN_ITEMS.CENTER,
                gap: SPACING.S12,
                padding: SPACING.S12,
                backgroundColor: Colors.WhiteSmoke,
                borderRadius: "10px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = Colors.VeryLightGrey;
                e.currentTarget.style.transform = "translateX(4px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = Colors.WhiteSmoke;
                e.currentTarget.style.transform = "translateX(0)";
              }}
            >
              <span style={{ fontSize: FONT_SIZE.F24 }}>{item.emoji}</span>
              <div>
                <p
                  style={{
                    fontSize: FONT_SIZE.F14,
                    fontFamily: FontFamily.ROBOTO_BOLD,
                    color: Colors.Black,
                    margin: 0,
                    marginBottom: "2px",
                  }}
                >
                  {item.title}
                </p>
                <p
                  style={{
                    fontSize: FONT_SIZE.F12,
                    fontFamily: FontFamily.ROBOTO_REGULAR,
                    color: Colors.Grey,
                    margin: 0,
                  }}
                >
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
export default Login;
